import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { Anchor, Badge, Card, Center, Container, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { getCompany, getCandidatesByCompany } from "../api";
import { CompanyLogo } from "../Candidates/CompanyLogo";
import { CandidateAvatar } from "../Candidates/CandidateAvatar";
import { formatCandidateName } from "../_Shared/candidateName";
import { careerDate } from "../_Shared/candidateRole";
import { useStore } from "../store";

// A position counts as "current" when it has no end date (or an open-ended marker).
function isCurrent(to) {
  if (!to || !to.trim()) return true;
  return ["present", "heute"].includes(to.trim().toLowerCase());
}

/**
 * Company page: header (logo + name + aliases + links) and the contacts who work or have worked
 * there, split into current vs. past. Tenant users see their tenant; GlobalAdmin sees every
 * tenant (merge inspect). Read-only; names link to the contact profile.
 */
export default function CompanyPage({ params }) {
  const { t } = useTranslation("Veft");
  const routeParams = useParams();
  const [, setLocation] = useLocation();
  const nameFormat = useStore((s) => s.nameFormat);
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const id = routeParams.id ?? params?.id;
  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      setCompany(null);
      return undefined;
    }
    setLoading(true);
    setContacts([]);
    // Company is global; people are tenant-scoped (or cross-tenant for GlobalAdmin).
    // Load them separately so a people-query failure still shows the company record
    // — that is the page an admin opens from the merge preview.
    getCompany(id)
      .then((co) => { if (!cancelled) setCompany(co); })
      .catch(() => { if (!cancelled) setCompany(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    getCandidatesByCompany(id)
      .then((people) => { if (!cancelled) setContacts(Array.isArray(people) ? people : []); })
      .catch(() => { if (!cancelled) setContacts([]); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <Center py="xl"><Loader /></Center>;
  if (!company) {
    return <Container size="sm" py="md"><Text c="dimmed">{t("company_not_found")}</Text></Container>;
  }

  // A contact may hold several positions at the same company over time (e.g. CFO, later supervisory
  // board) — show them ALL, newest-first (career arrays are already ordered that way). A contact counts
  // as "current" if ANY of their roles there is still open, so they appear once under "currently here".
  const rows = (contacts ?? []).map((c) => {
    const entries = (c.career ?? []).filter((e) => e.companyId === id);
    return { c, entries, current: entries.some((e) => isCurrent(e.to)) };
  });
  const current = rows.filter((r) => r.current);
  const past = rows.filter((r) => !r.current);

  const liUrl = company.linkedInUrl
    || (company.linkedInCompanyId ? `https://www.linkedin.com/company/${company.linkedInCompanyId}` : null);

  const renderRow = ({ c, entries }) => (
    <Group key={c.id} gap="sm" wrap="nowrap" align="flex-start" style={{ cursor: "pointer" }}
      onClick={() => setLocation(`/candidates/${c.id}`)}>
      <CandidateAvatar candidate={c} size="sm" radius="xl" color="blue" />
      <div style={{ minWidth: 0 }}>
        <Text size="sm" fw={500} truncate>{formatCandidateName(c, nameFormat)}</Text>
        {entries.length > 0 ? entries.map((e, i) => (
          <Text key={i} size="xs" c="dimmed" truncate>
            {e.role || c.title || ""}
            {e.from && ` · ${careerDate(e.from)} – ${isCurrent(e.to) ? t("present") : careerDate(e.to)}`}
          </Text>
        )) : (c.title && <Text size="xs" c="dimmed" truncate>{c.title}</Text>)}
      </div>
    </Group>
  );

  return (
    <Container size="sm" py="md">
      <Stack gap="lg">
        <Anchor size="sm" c="dimmed" onClick={() => window.history.back()} style={{ cursor: "pointer" }}>
          <Group gap={4} wrap="nowrap"><IconArrowLeft size={14} />{t("back")}</Group>
        </Anchor>

        <Group gap="md" align="flex-start" wrap="nowrap">
          <CompanyLogo companyId={company.id} companyName={company.name} size={56} radius="md" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Title order={3}>{company.name}</Title>
            <Group gap="md" mt={4}>
              {company.industry && <Text size="sm" c="dimmed">{company.industry}</Text>}
              {company.location && <Text size="sm" c="dimmed">{company.location}</Text>}
            </Group>
            {(company.aliases ?? []).length > 0 && (
              <Group gap={4} mt={8}>
                {(company.aliases ?? []).map((a, i) => (
                  <Badge key={i} size="xs" variant="light">{a}</Badge>
                ))}
              </Group>
            )}
            <Group gap="md" mt={6}>
              {company.website && (
                <Anchor size="xs" target="_blank" rel="noopener noreferrer"
                  href={company.website.startsWith("http") ? company.website : `https://${company.website}`}>
                  {t("website")}
                </Anchor>
              )}
              {liUrl && <Anchor size="xs" href={liUrl} target="_blank" rel="noopener noreferrer">LinkedIn</Anchor>}
            </Group>
          </div>
        </Group>

        <Card>
          <Title order={5} mb="md">{t("company_contacts", { count: rows.length })}</Title>
          {rows.length === 0 ? (
            <Text size="sm" c="dimmed">{t("no_company_contacts")}</Text>
          ) : (
            <Stack gap="lg">
              {current.length > 0 && (
                <Stack gap="xs">
                  <Text size="xs" tt="uppercase" c="dimmed" fw={600}>{t("currently_here")}</Text>
                  {current.map(renderRow)}
                </Stack>
              )}
              {past.length > 0 && (
                <Stack gap="xs">
                  <Text size="xs" tt="uppercase" c="dimmed" fw={600}>{t("previously_here")}</Text>
                  {past.map(renderRow)}
                </Stack>
              )}
            </Stack>
          )}
        </Card>
      </Stack>
    </Container>
  );
}
